import { Component, DestroyRef, inject, Input, OnInit, Optional } from '@angular/core';
import { BudgetForm, FormGroupFrom, IInvestmentWizard, InvestmentNotationStepState } from '../wizard-interface';
import { InvestmentWizardService } from '../../../../../@core/utils/investment-wizard-service';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NbAccordionModule, NbDialogRef, NbOptionModule, NbSelectModule, NbToggleModule } from '@nebular/theme';
import { ConsistencyService, InvestmentNotationsService, ScopeService } from '../../../../../@core/utils';
import { idMapToArray } from '../../../../../@core/utils/scope.service';
import { combineLatest, tap } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { KPI } from '../../../../../@core/api';

@Component({
  selector: 'app-wizard-investment-notation-step',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    NbToggleModule,
    NbOptionModule,
    NbSelectModule,
    NbAccordionModule,
  ],
  template: `
    <div class="wrapper">
      <form [formGroup]="notationForm" class="investment-form" autocomplete="off">
        <div class="form-group">
          <div class="form-field flex-row">
            <label for="enabled"
                   class="me-auto w-50"> </label>
            <div class="col-10">
              <span>
              {{ "Impact on equipment's technical state in {{year}}" | translate:{ year } }}
            </span>
            </div>
            <div class="col-2 text-end">
              <nb-toggle [checked]="notationForm.controls['enabled'].value"
                         (checkedChange)="formToggle($event)"></nb-toggle>
            </div>
          </div>

          @if (notationForm.controls['enabled'].value) {
            <div class="form-field">
              <label for="note">
                {{ "Technical state after investment" | translate }}
              </label>
              <nb-select
                id="note"
                formControlName="note"
                fullWidth
                [placeholder]="'Click here (obligatory info)' | translate"
              >
                @for (notation of STATE_LABELS; track notation.key) {
                  <nb-option [value]="notation.key">
                    {{ notation.label }}
                  </nb-option>
                }
              </nb-select>
            </div>
            <div class="form-field ">
              <div class="row w-100">
                <div class="col-8 m-0 mt-2">
                  {{ "Remaining lifetime (years) after the invest in {{ year }}" | translate:{ year } }}
                  <p>{{ remainingLifetime }}</p>
                </div>
                <div class="col-4 text-end mt-2">Source: {{ "calculated" | translate }}</div>
              </div>
            </div>
            <nb-accordion>
              <nb-accordion-item>
                <nb-accordion-item-header (click)="detailExpanded=!detailExpanded" class="text-dark">
                  <button type="button" class="btn btn-link text-dark">
                    @if (detailExpanded) {
                      {{ "Hide details" | translate }}
                    } @else {
                      {{ "More details" | translate }}
                    }
                  </button>
                </nb-accordion-item-header>
                <nb-accordion-item-body>
                  <!--                  Asset label-->
                  <div class="form-field">
                    <label for="asset-label">
                      {{ "reports.site-report.defaults.columns.asset_label" | translate }}
                    </label>
                    <div id="asset-label">{{ selectedAsset?.label }}</div>
                  </div>
                  <!--                  Installation year-->
                  <div class="form-field">
                    <label for="installation_year">
                      {{ "Installation year" | translate }}
                    </label>
                    <div id="installation_year">{{ selectedAsset?.installation_year }}</div>
                  </div>
                  <!--                  current age-->
                  <div class="form-field">
                    <label for="age">
                      {{ "Current age" | translate }} ({{ currentYear }})
                    </label>
                    <div id="age">{{ currentYear - selectedAsset?.installation_year }} {{ "year(s)" | translate }}</div>
                  </div>
                  <!--                  End of life year WITHOUT invest.-->
                  <div class="form-field">
                    <label for="eol-wo-invest">
                      {{ "End of life year WITHOUT investment" | translate }}
                    </label>
                    <div id="eol-wo-invest">{{ eolWithoutInvest }} </div>
                  </div>
                </nb-accordion-item-body>
              </nb-accordion-item>
            </nb-accordion>
          }
        </div>
      </form>
    </div>
  `,
  styleUrls: ['../wizard-index.component.scss', '../../../side-forms/base-form/base-form.component.scss'],
})
export class WizardInvestmentNotationStepComponent implements OnInit, IInvestmentWizard {
  readonly stateKey = 'INVESTMENT_NOTATION_STEP';
  state = () => this.wizardService.getState(this.stateKey);

  @Input() fromInvestmentForm: boolean = false;

  // dependencies
  private wizardService = inject(InvestmentWizardService);
  private consistencyService = inject(ConsistencyService);
  private scope = inject(ScopeService);
  private ivNotationService = inject(InvestmentNotationsService);

  // selected asset from previous step
  selectedAsset = this.wizardService.getState('ASSET_SELECTION');
  
  private destroyRef = inject(DestroyRef);

  detailExpanded = false;

  notationForm: FormGroupFrom<Pick<InvestmentNotationStepState, "note" | "enabled">> = new FormGroup({
    enabled: new FormControl<boolean>(true),
    note: new FormControl<0 | 22 | 46 | 70 | 94 | 100>(null, [Validators.required]),
  });

  private assetTypes = toSignal(this.scope.assetTypes$.pipe(idMapToArray(), takeUntilDestroyed()), { initialValue: [] });
  STATE_LABELS = this.ivNotationService.STATE_LABELS;
  year!: number;
  eolWithoutInvest!: number;

  currentYear = new Date().getFullYear();

  remainingLifetime: number;

  constructor(
    @Optional() public ref: NbDialogRef<WizardInvestmentNotationStepComponent>
  ) {}

  ngOnInit(): void {
    const investmentState = this.wizardService.getState('BUDGET_ALLOCATION'),
      ivType = this.wizardService.getState('INVESTMENT_TYPE_SELECTION');

    if (!investmentState) throw Error(`Investment state not found in wizard service`);
    this.year = investmentState.final_schedule_to;
    this.wizardService.blockCurrentStep('Please fill in all required fields');

    this.notationForm.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((value) => {
      if (!value.enabled || (value.enabled && this.notationForm.valid)) {
        this.wizardService.unblockCurrentStep();
      } else {
        this.wizardService.blockCurrentStep('Please fill in all required fields');
      }
      const assetType = this.assetTypes().find(at => at.id === this.selectedAsset?.asset_type);
      if (value.note){
        const {eol_without_investment, lifetime_after_investment} = this.getInvestmentNoteDetail(this.selectedAsset, assetType, investmentState, value.note)
        const duration_deviation = lifetime_after_investment - (eol_without_investment - this.currentYear);
        this.wizardService.setState(this.stateKey, <InvestmentNotationStepState>{ ...value, duration_deviation, lifetime_after_investment });
      } else {
        this.wizardService.setState(this.stateKey, <InvestmentNotationStepState>{ ...value});
      }
    });

    // Subscribe to note changes to update calculated fields
    combineLatest([
      this.notationForm.controls.note.valueChanges,
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(([note]) => {
        const assetType = this.assetTypes().find(at => at.id === this.selectedAsset?.asset_type);
        const {eol_without_investment, lifetime_after_investment} = this.getInvestmentNoteDetail(this.selectedAsset, assetType, investmentState, note);
        this.eolWithoutInvest = eol_without_investment
        this.remainingLifetime = lifetime_after_investment
      })).subscribe();

    if (this.state()) {
      this.notationForm.patchValue(this.state())
    } else if (ivType.renew) {
      const note = 100;
      this.notationForm.setValue({
        note, enabled: true,
      });
    }
  }

  formToggle($event: boolean) {
    this.notationForm.get('enabled').setValue($event);
  }

  private getInvestmentNoteDetail(asset: KPI.Asset, assetType: KPI.AssetType, investmentState: BudgetForm, note: number): {lifetime_after_investment: number, eol_without_investment: number} {
    const eolWithoutInvest = asset.installation_year + (assetType?.expected_duration || 0) + asset.duration_deviation || 0,
    alphaCoeff = assetType?.alpha_coefficient || 4,
    remainingLifetime = this.consistencyService.getEstimatedRemainingLifetime(
      investmentState.final_schedule_to,
      investmentState.final_schedule_to,
      note,
      assetType.expected_duration,
      alphaCoeff,
    );
    return {
      eol_without_investment: eolWithoutInvest,
      lifetime_after_investment: remainingLifetime,
    };
  }
}
